import { useEffect, useRef, useState, useCallback } from 'react';
import { fetchFileBytes } from './fetchFile';
import {
  highlightSnippet,
  findTextMatches,
  wrapMatches,
  clearMarks,
  scrollMarkIntoView,
  CITE_CLASS,
  SEARCH_CLASS,
  SEARCH_ACTIVE_CLASS,
} from './highlightSnippet';

// Plain-text and RTF renderer. RTF is shown as the raw markup since we
// don't bundle an RTF parser — that's what mammoth/docx is for. Most
// users uploading .rtf will accept this; truly readable RTF goes through
// the Phase 6 LibreOffice fallback.
export default function TextPreview({ documentId, query = null, onHighlightStatus = null }) {
  const [text, setText] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const preRef = useRef(null);

  const [searchVal, setSearchVal] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCursor, setSearchCursor] = useState(0);
  const searchMarksRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setLoadError(null);

    fetchFileBytes(documentId, 'txt')
      .then(buf => {
        if (cancelled) return;
        setText(new TextDecoder('utf-8').decode(buf));
      })
      .catch(err => { if (!cancelled) setLoadError(err.message); });

    return () => { cancelled = true; };
  }, [documentId]);

  // Apply citation highlight once the text node is in the DOM.
  // Container-scoped scroll + honest status — see DocxPreview.
  useEffect(() => {
    if (text == null || !preRef.current || !query) return;
    const { marks } = highlightSnippet(preRef.current, query, CITE_CLASS, { scroll: false });
    if (marks[0]) requestAnimationFrame(() => scrollMarkIntoView(marks[0]));
    if (onHighlightStatus) onHighlightStatus(marks.length > 0 ? 'located' : 'no-location');
  }, [text, query, onHighlightStatus]);

  const applyActiveSearchMark = useCallback((idx) => {
    const anchors = searchMarksRef.current;
    const container = preRef.current;
    if (!anchors || anchors.length === 0 || !container) return;
    container.querySelectorAll(`mark.${SEARCH_CLASS}`).forEach((m) => {
      const mi = parseInt(m.dataset.matchIdx, 10);
      if (mi === idx) m.classList.add(SEARCH_ACTIVE_CLASS);
      else m.classList.remove(SEARCH_ACTIVE_CLASS);
    });
    const active = anchors[idx];
    if (active) active.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const runSearch = useCallback((q) => {
    setSearchQuery(q);
    setSearchCursor(0);
    if (preRef.current) clearMarks(preRef.current, SEARCH_CLASS);
    searchMarksRef.current = [];
    if (!q || q.trim().length < 2 || !preRef.current) return;
    const matches = findTextMatches(preRef.current, q);
    searchMarksRef.current = wrapMatches(matches, SEARCH_CLASS);
    if (searchMarksRef.current.length > 0) applyActiveSearchMark(0);
  }, [applyActiveSearchMark]);

  const total = searchMarksRef.current.length;
  const goPrev = () => {
    if (total === 0) return;
    const n = (searchCursor - 1 + total) % total;
    setSearchCursor(n);
    applyActiveSearchMark(n);
  };
  const goNext = () => {
    if (total === 0) return;
    const n = (searchCursor + 1) % total;
    setSearchCursor(n);
    applyActiveSearchMark(n);
  };

  return (
    <div className="dd-preview text-preview">
      {text != null && (
        <div className="dd-preview-nav">
          <span style={{ flex: 1 }} />
          <div className="dv-search-box">
            <input
              className="dv-search-input"
              type="search"
              placeholder="Search…"
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch(searchVal);
                if (e.key === 'Escape') { setSearchVal(''); runSearch(''); }
              }}
              aria-label="Search in document"
            />
            <span className="dv-search-count">
              {total > 0 ? `${searchCursor + 1} / ${total}` : (searchQuery ? '0' : '')}
            </span>
            <button className="btn" onClick={goPrev} disabled={total === 0} title="Previous match">↑</button>
            <button className="btn" onClick={goNext} disabled={total === 0} title="Next match">↓</button>
          </div>
        </div>
      )}
      {loadError && <p className="text-muted" style={{ padding: 16 }}>Failed to load text: {loadError}</p>}
      {!loadError && text == null && <div className="dd-loading" style={{ padding: 16 }}>Loading text…</div>}
      {text != null && <pre ref={preRef} className="text-preview-pre">{text}</pre>}
    </div>
  );
}
